import type { Participant } from '../types';
import type { ParticipantData } from '../types/meeting';

/**
 * Builds a Participant model from participant data.
 */
export function mapParticipant(
  data: ParticipantData,
  existing?: Participant
): Participant {
  const preferLocalCameraState = data.isLocal && existing;
  const isLocal = data.isLocal === true;
  const candidateVideoTrack = data.videoTrack ?? existing?.videoTrack;
  const remoteHasTrack = !isLocal && !!candidateVideoTrack;
  const cameraOn = preferLocalCameraState
    ? existing!.isCameraOn
    : data.isCameraOn || remoteHasTrack;
  const resolvedVideoTrack =
    cameraOn && (isLocal ? isLiveMediaTrack(candidateVideoTrack) : !!candidateVideoTrack)
      ? candidateVideoTrack
      : undefined;

  return {
    participantId: data.participantId,
    displayName: data.displayName,
    avatarUrl: data.avatarUrl ?? existing?.avatarUrl,
    isVR: data.isVR,
    isHost: data.isHost,
    isMuted: data.isMuted,
    isCameraOn: cameraOn,
    isSharingScreen: data.isSharingScreen,
    videoTrack: resolvedVideoTrack,
    audioTrack: data.audioTrack ?? existing?.audioTrack,
  };
}

/**
 * Adds or updates a participant in the list.
 */
export function upsertParticipant(
  participants: Participant[],
  data: ParticipantData
): Participant[] {
  const index = participants.findIndex((p) => p.participantId === data.participantId);

  if (index === -1) {
    return [...participants, mapParticipant(data)];
  }

  const updated = mapParticipant(data, participants[index]);
  return participants.map((p, i) => (i === index ? updated : p));
}

/**
 * Removes a participant from the list.
 */
export function removeParticipant(
  participants: Participant[],
  participantId: string
): Participant[] {
  return participants.filter((p) => p.participantId !== participantId);
}

/**
 * Updates audio/video tracks for a participant.
 */
export function updateParticipantTrack(
  participants: Participant[],
  participantId: string,
  trackType: string,
  track: MediaStreamTrack | null
): Participant[] {
  return participants.map((participant) =>
    participant.participantId === participantId
      ? applyRemoteTrackUpdate(participant, trackType, track)
      : participant
  );
}

/**
 * Updates audio/video tracks for the local participant.
 */
export function updateLocalParticipantTrack(
  participant: Participant | null,
  participantId: string,
  trackType: string,
  track: MediaStreamTrack | null
): Participant | null {
  if (!participant || participant.participantId !== participantId) {
    return participant;
  }

  return applyTrackUpdate(participant, trackType, track);
}

/**
 * Updates screen share flags for a participant.
 */
export function updateParticipantSharing(
  participants: Participant[],
  participantId: string,
  isSharing: boolean
): Participant[] {
  return participants.map((participant) =>
    participant.participantId === participantId
      ? { ...participant, isSharingScreen: isSharing }
      : participant
  );
}

function applyTrackUpdate(
  participant: Participant,
  trackType: string,
  track: MediaStreamTrack | null
): Participant {
  if (trackType === 'video') {
    const trackLive = isLiveMediaTrack(track);
    const cameraOn = participant.isCameraOn;
    return {
      ...participant,
      videoTrack: cameraOn && trackLive ? track ?? undefined : undefined,
      isCameraOn: track == null ? false : cameraOn,
    };
  }

  if (trackType === 'audio') {
    return {
      ...participant,
      audioTrack: track ?? undefined,
    };
  }

  return participant;
}

function applyRemoteTrackUpdate(
  participant: Participant,
  trackType: string,
  track: MediaStreamTrack | null
): Participant {
  if (trackType === 'video') {
    return {
      ...participant,
      videoTrack: track ?? undefined,
      isCameraOn: track != null,
    };
  }

  if (trackType === 'audio') {
    return {
      ...participant,
      audioTrack: track ?? undefined,
    };
  }

  return participant;
}

/**
 * Determines whether a media track is actively usable for rendering.
 */
function isLiveMediaTrack(track: MediaStreamTrack | null | undefined): boolean {
  if (!track) {
    return false;
  }

  if (track.readyState !== 'live') {
    return false;
  }

  if (!track.enabled || track.muted) {
    return false;
  }

  return true;
}
